import { useMsal } from "@azure/msal-react";
import type {
  AccountInfo,
  AuthenticationResult,
  PopupRequest,
} from "@azure/msal-browser";
import { useCallback, useEffect, useMemo } from "react";
import { loginRequest } from "@/shared/config/msal";

// Maps JumpCloud group names to application roles. Keep in sync with the API's
// "RoleMappings" section in appsettings.json.
const GROUP_ROLE_MAP: Record<string, string> = {
  "SSO - Talent Finder Admin": "Admin",
};

// Normalized lookup so matching tolerates casing and whitespace drift between
// JumpCloud group names and the keys above.
const normalizeGroup = (value: string): string => value.trim().toLowerCase();

const NORMALIZED_GROUP_ROLE_MAP = new Map(
  Object.entries(GROUP_ROLE_MAP).map(([group, role]) => [
    normalizeGroup(group),
    role,
  ]),
);

function mapGroupToRole(value: string): string {
  return NORMALIZED_GROUP_ROLE_MAP.get(normalizeGroup(value)) ?? value.trim();
}

export function extractRolesFromAccount(
  account?: AccountInfo | null,
): string[] {
  if (!account?.idTokenClaims) {
    return [];
  }

  const claims = account.idTokenClaims as Record<string, unknown>;
  // JumpCloud emits group memberships under the configured claim name (here
  // "memberOf") and returns a single group as a string, multiple as an array.
  const raw = claims.memberOf;
  let values: string[] = [];
  if (Array.isArray(raw)) {
    values = raw.filter((role): role is string => typeof role === "string");
  } else if (typeof raw === "string") {
    values = [raw];
  }

  return [...new Set(values.map(mapGroupToRole).filter(Boolean))];
}

export function getDisplayNameFromAccount(
  account?: AccountInfo | null,
): string {
  if (account?.name && typeof account.name === "string") {
    return account.name;
  }

  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
  const preferredUsername = claims?.preferred_username;
  if (typeof preferredUsername === "string") {
    return preferredUsername;
  }

  if (account?.username && typeof account.username === "string") {
    return account.username;
  }

  return "User";
}

export function useAuth() {
  const { instance, accounts } = useMsal();
  const resolvedAccount = useMemo(
    () => instance.getActiveAccount() ?? accounts.at(0) ?? null,
    [instance, accounts],
  );

  useEffect(() => {
    if (!resolvedAccount) {
      return;
    }
    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(resolvedAccount);
    }
  }, [instance, resolvedAccount]);

  const loginPopup = useCallback(
    async (loginHint?: string): Promise<AuthenticationResult | null> => {
      const request: PopupRequest = {
        ...(loginRequest as PopupRequest),
        ...(loginHint ? { loginHint } : {}),
      };

      try {
        const response = await instance.loginPopup(request);
        instance.setActiveAccount(response.account);
        return response;
      } catch (error) {
        console.error("Login popup error:", error);
        throw error;
      } finally {
        const account = instance.getActiveAccount();
        if (!account) {
          const allAccounts = instance.getAllAccounts();
          if (allAccounts.length > 0) {
            instance.setActiveAccount(allAccounts[0]);
          }
        }
      }
    },
    [instance],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await instance.logoutRedirect({
        account: resolvedAccount ?? undefined,
        postLogoutRedirectUri: `${window.location.origin}/auth/login`,
      });
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  }, [instance, resolvedAccount]);

  const isAuthenticated = useMemo(
    () => Boolean(resolvedAccount),
    [resolvedAccount],
  );

  const roles = useMemo(
    () => extractRolesFromAccount(resolvedAccount),
    [resolvedAccount],
  );

  const displayName = useMemo(
    () => getDisplayNameFromAccount(resolvedAccount),
    [resolvedAccount],
  );

  const getRoles = useCallback(() => roles, [roles]);

  return {
    loginPopup,
    logout,
    isAuthenticated,
    account: resolvedAccount as AccountInfo | null,
    roles,
    displayName,
    getRoles,
  };
}
